import type { SprintRoute } from '@waago/api'

/**
 * Which sprint delivers each screen of the client app.
 *
 * <p>Derived from `WaaGo_Matrice_Fonctionnalites_Sprints.xlsx`, which assigns
 * every one of the matrix's 115 refs to a milestone (M1–M10) per the signed
 * Consulting Agreement and `implementation/ROADMAP-40-DAYS.md` — not from an
 * internal estimate. Anything not listed is treated as sprint 1 and stays
 * visible, so a screen nobody classified shows up and gets noticed rather
 * than disappearing quietly.
 *
 * <p>**This is the file to edit if the split is wrong.** One line moves a
 * screen between sprints; nothing else needs to change. For *why* a given
 * ref landed in a given sprint, check that workbook's Commentaire column —
 * every ref the contract doesn't name outright carries a note there.
 *
 * <p>The judgement calls worth knowing about, since the workbook's rows are
 * features (one `Réf.` per row) and these are routes:
 *
 * - **The whole request-creation flow is Sprint 1**, including transport,
 *   livraison, location and conducteur (AC-014/015/016/018/019/021..025) —
 *   all four activities are M2 work bar the tracking tail.
 * - **`/demandes/:id` and its messages are Sprint 3.** Opening a mission is
 *   live tracking and chat (AC-017/020, M5), even though creating it was
 *   Sprint 1 — `/demandes/:id/postee`, the posted confirmation, stays in
 *   Sprint 1 since it is the tail of creation, not of tracking.
 * - **Payment is Sprint 3** end to end (AC-027/028/029/030/031, M6).
 * - **`/compte/langue` and `/compte/permission` are Sprint 1** alongside the
 *   rest of the account cluster — AC-034 ("Paramètres") is M1, not the later
 *   milestone the old roadmap-only mapping assumed.
 * - **Aide, contact, mentions légales and signalement are all Sprint 4** —
 *   M8's scope of work explicitly names "contact + signalement forms · terms
 *   & conditions" alongside AC-035, even though only AC-035/009 get their own
 *   ref.
 */
export const CLIENT_SPRINTS: SprintRoute[] = [
  // --- Sprint 1 · M1 Account/Profile/Settings + M2 Media & Geo -----------
  { path: '/', sprint: 1 }, // AC-007 Page d'accueil
  { path: '/bienvenue', sprint: 1 }, // AC-001 Splash & onboarding
  { path: '/connexion', sprint: 1 }, // AC-002 Inscription téléphone + OTP
  { path: '/inscription', sprint: 1 }, // AC-003/005 Création compte
  { path: '/inscription/type', sprint: 1 },
  { path: '/bienvenue-compte', sprint: 1 },
  { path: '/compte', sprint: 1 }, // AC-032/036 Mon profil, déconnexion
  { path: '/compte/profil', sprint: 1 },
  { path: '/compte/securite', sprint: 1 }, // AC-006 Mot de passe oublié
  { path: '/compte/securite/numero', sprint: 1 },
  { path: '/compte/utilisateurs', sprint: 1 }, // AC-005 Compte Professionnel
  { path: '/compte/langue', sprint: 1 }, // AC-034 Paramètres
  { path: '/compte/permission', sprint: 1 },
  { path: '/activite', sprint: 1 }, // AC-033 Historique activités
  { path: '/recherche', sprint: 1 }, // AC-010 Recherche prestataires
  { path: '/demandes/filtre', sprint: 1 },
  { path: '/prestataire/:id', sprint: 1 }, // AC-011 Fiche prestataire
  { path: '/prestataire/:id/avis', sprint: 1 }, // AC-012 Système d'avis
  { path: '/demande/:activity', sprint: 1 }, // AC-014/015/016/018/019/021..025
  { path: '/demandes/:id/postee', sprint: 1 },

  // --- Sprint 3 · M5 Tracking & Messaging, M6 Money, M7 Back-Office ------
  { path: '/demandes/:id', sprint: 3 }, // AC-017/020 Suivi GPS/livraison
  { path: '/demandes/:id/messages', sprint: 3 },
  { path: '/demandes/:id/paiement', sprint: 3 }, // AC-027/028/030
  { path: '/demandes/:id/paiement/confirme', sprint: 3 },
  { path: '/compte/paiement', sprint: 3 }, // AC-031 Historique paiements
  { path: '/compte/paiement/ajouter', sprint: 3 },

  // --- Sprint 4 · M8 Notifications & Support ------------------------------
  { path: '/notifications', sprint: 4 }, // AC-009
  { path: '/aide', sprint: 4 }, // AC-035 Aide & Support
  { path: '/aide/contact', sprint: 4 },
  { path: '/mentions-legales', sprint: 4 },
  { path: '/signalement', sprint: 4 },
  { path: '/signalement/:id', sprint: 4 },
  { path: '/demandes/:id/signalement', sprint: 4 },
]
